import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useForm, UseFormReturn } from 'react-hook-form';
import {
  $cardListStore,
  $initialAccountsStore,
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsGetFxRateStore,
  $transferRequestsUpdateStore,
  $transferUpdateStatusStore,
} from '@lib/store';
import { AmountUtils, RoutePaths } from '@shared/utils';
import type { ApiResponse } from '@shared/api/models';
import { TransferRecipientTypeEnum } from '@shared/api/rest/recipients';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import { TransferRequestResponse, TransferRequestStatusEnum, TransferRequestTypeEnum } from '@shared/api/rest/transferRequests';
import { TransferConfirmationBlock } from './widgets';
import { PaymentFormBlock, PaymentFormDataType } from './formItem';
import { Icon, Button, PageContainer, Text, ErrorMessage, SelectAccount } from '@shared/widgets';
import { Constants } from '@shared/constants';
import ArrowIcon from '@assets/icons/arrow.svg';

export interface FormSchemaCard {
  purpose: string;
  amount: number;
  beneficiaryAmount?: number;
  paymentReason: string;
}

export const CardSettlementPage = () => {
  const accountStore = useUnit($initialAccountsStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferRequestsUpdateStore = useUnit($transferRequestsUpdateStore.store);
  const transferReasonsStore = useUnit($transferReasonsStore.store);
  const fxRateStore = useUnit($transferRequestsGetFxRateStore.store);

  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [modal, setModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [otpModal, setOTPModal] = useState<TransferRequestResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [errorSelect, setErrorSelect] = useState<string>('');
  const [accountTo, setAccountTo] = useState<AccountResponse>();
  const [createdTransferRequest, setCreatedTransferRequest] = useState<ApiResponse<TransferRequestResponse>>();

  const formControl = useForm<FormSchemaCard>();

  useEffect(() => {
    $transferReasonsStore.events.getTransferReasons();
    $cardListStore.events.update({ Count: 100 });

    return () => {
      $transferRequestsCreateStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (accountStore.data.length === 0) return;
    setAccount(accountStore.data[0]);
  }, [accountStore.data]);

  const openModal = async () => {
    !accountTo ? setErrorSelect('Please select card') : '';
    formControl.handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: FormSchemaCard) => {
    if (!account || !accountTo) return;

    try {
      if (createdTransferRequest?.data) {
        const updatedTransferRequest = await $transferRequestsUpdateStore.events.update({
          id: createdTransferRequest.data.id,
          type: TransferRequestTypeEnum.CardAccountSettlement,
          fromAccountId: account?.id,
          beneficiaryCurrency: account?.currency,
          purpose: data.purpose,
          beneficiaryAccountId: accountTo?.id,
          senderAmount: data.amount,
          totalAmount: data.amount,
          beneficiaryAmount: data.beneficiaryAmount,
          reasonOfThePayment: data.paymentReason,
          beneficiaryCountryCode: 'USA',
          fastPayment: true,
        });

        setCreatedTransferRequest(updatedTransferRequest);
        setModal(true);
        return;
      }
      const updatedTransferRequest = await $transferRequestsCreateStore.events.create({
        type: TransferRequestTypeEnum.CardAccountSettlement,
        fromAccountId: account?.id,
        beneficiaryCurrency: account?.currency,
        purpose: data.purpose,
        beneficiaryAccountId: accountTo?.id,
        senderAmount: data.amount,
        totalAmount: data.amount,
        beneficiaryAmount: data.beneficiaryAmount,
        reasonOfThePayment: data.paymentReason,
        fastPayment: true,
      });
      setCreatedTransferRequest(updatedTransferRequest);
      setModal(true);
    } catch {
      if (createdTransferRequest == null) setError('error');
    }
  };

  const closeModal = () => {
    setModal(false);
  };
  const openConfirmModal = () => {
    setModal(false);
    setConfirmModal(true);
  };
  const onDocumentsConfirm = async () => {
    if (transferRequestsCreateStore.data) {
      if (transferRequestsCreateStore?.data?.id) {
        try {
          await $transferUpdateStatusStore.events.update({
            id: transferRequestsCreateStore?.data?.id,
            status: TransferRequestStatusEnum.WaitingForApproval,
          });
          openConfirmModal();
        } catch {
          setError('error');
          openConfirmModal();
        }
      }
    }
  };

  useEffect(() => {
    if (account?.currency && accountTo?.currency && account.id)
      $transferRequestsGetFxRateStore.events.update({
        AccountId: account.id,
        FromCurrency: account.currency,
        ToCurrency: accountTo?.currency,
        CountryCode: 'DE',
      });
  }, [accountTo?.currency, account?.currency]);

  const handleRequestFee = async () => {
    if (amount && accountTo) {
      await $transferRequestsFeeStore.events.update({
        AccountId: account?.id,
        SenderAmount: amount,
        FastPayment: true,
        BeneficiaryCurrencyCode: accountTo?.currency,
        BeneficiaryBankCountryCode: 'USA',
        TransferRequestType: TransferRequestTypeEnum.CardAccountSettlement,
      });
    }
  };

  useEffect(() => {
    handleRequestFee();
  }, [account?.id, accountTo]);

  const amount = formControl.watch('amount');
  const paymentReason = formControl.watch('paymentReason');

  const reasonOptions = useMemo(() => {
    const bankCountryCode = Constants.BANK_COUNTRY_CODE;
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Business));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data]);

  const total = amount! + (feeStore?.data || 0);

  return (
    <PageContainer
      title='Payments'
      header={
        <div className='flex gap-2 items-center'>
          <Link to={RoutePaths.payments.onNavigate()}>
            <Icon icon={<ArrowIcon />} className='rotate-180' />
          </Link>
          <Text weight='semibold' size='2xl'>
            Payments
          </Text>
        </div>
      }
    >
      <div className='flex flex-col w-full'>
        <Text className='mb-6' weight='semibold' size='2xl'>
          Card Account Settlement
        </Text>
        <div className='p-6 bg-white dark:bg-soft-black'>
          <SelectAccount
            loading={accountStore.loading}
            accounts={accountStore.data}
            label='Transfer From'
            currentAccount={account}
            type={AccountType.Payment}
            onChange={setAccount}
            balanceFieldName='availableBalance'
          />
        </div>
        <form className='flex flex-col gap-6' onSubmit={formControl.handleSubmit(onSubmit)}>
          <div className='dark:bg-soft-black bg-white mt-6 p-6 flex flex-col gap-2'>
            <div>
              <SelectAccount
                loading={accountStore.loading}
                accounts={accountStore?.data.filter((a) => a.currency === 'EUR').filter((acc) => acc.id !== account?.id)}
                label='Please select a card account to settle'
                currentAccount={accountTo}
                type={AccountType.Card}
                onChange={setAccountTo}
                balanceFieldName='availableBalance'
              />
              {accountTo ? '' : <ErrorMessage text={errorSelect} />}
            </div>
          </div>
          <PaymentFormBlock
            formControl={formControl as UseFormReturn<PaymentFormDataType, undefined>}
            handleRequestFee={handleRequestFee}
            feeData={feeStore.data}
            feeLoading={feeStore.loading}
            amount={amount}
            totalAmount={total}
            currency={account?.currency}
            reasonOptions={reasonOptions}
            exchangeRate={fxRateStore}
            account={account}
            accountTo={accountTo}
          />
        </form>
        <div className='flex mt-3 gap-3'>
          <Button
            color='primary'
            className='!gap-4 rounded'
            disabled={feeStore.loading || transferRequestsCreateStore.loading || transferRequestsUpdateStore.loading}
            onClick={openModal}
          >
            Transfer {amount! > 0 && `${AmountUtils.transformToAmountWithCurrency(total, account?.currency as string)}`}
          </Button>
        </div>
      </div>
      {transferRequestsCreateStore.data?.id && (
        <TransferConfirmationBlock
          amountVal={amount}
          reason={paymentReason}
          closeModal={closeModal}
          account={account!}
          accountTo={accountTo}
          onDocumentsConfirm={onDocumentsConfirm}
          transactionId={transferRequestsCreateStore.data?.id}
          closeOTPModal={() => setOTPModal(null)}
          purpose={reasonOptions.find((reason) => reason.value === formControl.watch('purpose'))?.label || ''}
          otpModal={otpModal}
          openConfirmModal={openConfirmModal}
          confirmModal={confirmModal}
          error={error}
          modal={modal}
          fee={feeStore.data || 0}
          exchangeRate={fxRateStore.data!}
          transferRequest={createdTransferRequest}
          isFastPayment
        />
      )}
    </PageContainer>
  );
};
